import React, { useEffect }                                 from 'react';
import { Switch, Route, useLocation, useHistory, Redirect } from 'react-router-dom';
import { RouterUtility }                                    from '../util';
import { Userpilot }                                        from 'userpilot';

import { ROUTES }       from '../constants/RouteEnums';
import { PrivateRoute } from './components/util/routing/PrivateRoute';

import { RouterLocationState }                               from '../types';
import { useEntityRouting, useLastRedirectURL, usePrevious } from '../hooks/';
import NotFoundPage                                          from './pages/404/NotFoundPage';
import LoginPage                                             from './pages/account/login/LoginPage';
import LogoutPage                                            from './pages/account/logout/LogoutPage';
import AccountNotFoundPage                                   from './pages/account/notFound/AccountNotFoundPage';
import AppIndexPage                                          from './pages/app/AppIndexPage';
import AdminIndexPage                                        from './pages/admin/AdminIndexPage';
import DecisionEngineManagerPage                             from './pages/admin/decision-engine/DecisionEngineManagerPage';
import SystemAccountingManagerPage                           from './pages/admin/system-accounting/SystemAccountingManagerPage';
import ProjectDashboardIndexPage                             from './pages/projects/overviews/project-dashboard/ProjectDashboardIndexPage';
import ProjectIndexPage                                      from './pages/projects/ProjectIndexPage';
import UserIndexPage                                         from './pages/users/UserIndexPage';
import CurrentUserIndexPage                                  from './pages/users/CurrentUserIndexPage';
import DashContainerPage                                     from './pages/projects/dash/DashContainerPage';
import DoraAIAssistantPage                                   from './pages/ai/DoraAIAssistantPage';
import ConsultantPage                                        from './pages/consultant-dash/ConsultantPage';
import { SystemAdminsOnly }                                  from './components/util/access/RoleAccessControl';

const routeTo = RouterUtility.getRoutePath;

const Router = React.memo( () => {

	let history                                    = useHistory<RouterLocationState>(),
	    location                                   = useLocation<RouterLocationState>(),
	    prevLocation                               = usePrevious( location ),
	    { setLastRedirectURL, getLastRedirectURL } = useLastRedirectURL(),
	    { updateRouterURLSilently }                = useEntityRouting();

	// set up role access control props w/ project's context object
	// so we dont need to keep specifying project / org,
	// otherwise we'd need to speciy id's on each control
	let adminAccessProps = {
		redirect: routeTo( 'HOME' ) as string,
	};

	// when route changes...
	useEffect( () => {

		if( prevLocation !== undefined ){

			//@ts-ignore
			if( prevLocation.pathname && prevLocation.pathname !== location.pathname ){

				history.location.state = { ...history.location.state, referrer: prevLocation }
			}
		}
	} );

	// when route changes...
	useEffect( () => {

		// register a history listener and return the unsubscribe callback
		return history.listen( ( location ) => {

			// console.log( `heap.track(${ location.pathname })` );

		} )

	}, [ history ] );

	return (
		<Switch>
			<PrivateRoute path={ routeTo( 'HOME' ) } exact={ true } render={ ( routerProps ) => {
				updateRouterURLSilently( routeTo( 'DASHBOARD' ) as string );
				return <ProjectDashboardIndexPage/>
			} }/>

			{/* ACCOUNT */ }
			<Route path={ routeTo( 'LOGIN' ) } exact={ true } component={ LoginPage }/>
			<Route path={ routeTo( 'LOGOUT' ) } exact={ true } component={ LogoutPage }/>
			<Route path={ routeTo( 'NO_ACCOUNT' ) } exact={ true } component={ AccountNotFoundPage }/>

			{/* PROJECTS */ }
			<PrivateRoute path={ routeTo( 'DASHBOARD' ) } exact={ true } component={ ProjectDashboardIndexPage }/>

			{/* PROJECT */ }
			<PrivateRoute path={ `${ routeTo( 'PROJECT' ) }/:section?` } component={ ProjectIndexPage }/>

			{/* USERS */ }
			<PrivateRoute path={ `${ routeTo( 'USERS' ) }/:section?` } component={ UserIndexPage }/>
			<PrivateRoute path={ `${ routeTo( 'CURRENT_USER' ) }/:section?` } component={ CurrentUserIndexPage }/>
		<PrivateRoute path={ `${ routeTo( 'DASH' ) }/:section?` } component={ DashContainerPage }/>
		<PrivateRoute path={ `${ routeTo( 'PROJECT.DASH' ) }/:section?` } component={ DashContainerPage }/>
		<PrivateRoute path={ routeTo( 'DORA_AI' ) } exact={ true } component={ DoraAIAssistantPage }/>
		<PrivateRoute path={ routeTo( 'CONSULTANT' ) } exact={ true } component={ ConsultantPage }/>
			{/* ADMIN */ }
			<PrivateRoute path={ routeTo( 'ADMIN' ) } exact={ true } component={ ( props: any ) => (
				<SystemAdminsOnly { ...adminAccessProps }>
					<AdminIndexPage { ...props } />
				</SystemAdminsOnly>
			) }/>
			<PrivateRoute path={ `${ routeTo( 'ADMIN.DECISION_ENGINE' ) }/:section?` } component={ ( props: any ) => (
				<SystemAdminsOnly { ...adminAccessProps }>
					<DecisionEngineManagerPage { ...props } />
				</SystemAdminsOnly>
			) }/>
			<PrivateRoute path={ `${ routeTo( 'ADMIN.SYSTEM_ACCOUNTING' ) }/:section?` } component={ ( props: any ) => (
				<SystemAdminsOnly { ...adminAccessProps }>
					<SystemAccountingManagerPage { ...props } />
				</SystemAdminsOnly>
			) }/>

			<Route component={ NotFoundPage }/>

		</Switch>
	);
} );

export { Router, ROUTES as RouterPaths };
